import { defineStore, storeToRefs } from 'pinia';
import { reactive } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useDocumentEditor } from '@/stores/document/editor';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { generateJobProfileSummaryOptions, generateJobProfileSummaryRefine } from '@/api/dfd';

export const SUMMARY_OPTION_KEYS = ['default', 'more_specific', 'more_junior', 'more_senior'];

const emptyOption = () => ({ summary: null, error: null, loading: false });

export const useContextualProfileSummary = defineStore('contextualProfileSummary', () => {
  const { jobProfile, summaryFieldId } = storeToRefs(useCurrentJobProfile());
  const { addErrorSnackbar } = useAppSnackbar();
  const { tiptap } = storeToRefs(useDocumentEditor());

  const summaryModal = reactive({
    isOpen: false,
    selected: 'default',
    options: Object.fromEntries(SUMMARY_OPTION_KEYS.map((key) => [key, emptyOption()])),
  });

  // for the case when modal is closed and reopened before the first request is completed
  let requestId = 0;

  const resetOptions = () => {
    SUMMARY_OPTION_KEYS.forEach((key) => { summaryModal.options[key] = emptyOption(); });
  };

  const loadSummaryOptions = async () => {
    const currentRequest = ++requestId;
    SUMMARY_OPTION_KEYS.forEach((key) => {
      summaryModal.options[key] = { ...emptyOption(), loading: true };
    });

    try {
      const { summaries } = await generateJobProfileSummaryOptions(jobProfile.value?.ulid);
      if (currentRequest !== requestId) return;

      SUMMARY_OPTION_KEYS.forEach((key) => {
        const { summary = null, error = null } = summaries[key] || { error: 'generation_failed' };
        summaryModal.options[key] = { summary, error, loading: false };
      });
    } catch (err) {
      if (currentRequest !== requestId) return;

      SUMMARY_OPTION_KEYS.forEach((key) => {
        summaryModal.options[key] = { ...emptyOption(), error: 'generation_failed' };
      });

      addErrorSnackbar({ text: `Failed to generate summary: ${err.message || err}` });
      throw err;
    }
  };

  const retrySummaryOption = async (key) => {
    const baseSummary = summaryModal.options.default.summary;
    if (key === 'default' || !baseSummary) {
      loadSummaryOptions();
      return;
    }

    const currentRequest = requestId;
    summaryModal.options[key] = { ...emptyOption(), loading: true };

    try {
      const { summary } = await generateJobProfileSummaryRefine(jobProfile.value?.ulid, {
        operation: key,
        baseSummary,
      });
      if (currentRequest !== requestId) return;

      summaryModal.options[key] = summary
        ? { ...emptyOption(), summary }
        : { ...emptyOption(), error: 'generation_failed' };
    } catch (err) {
      if (currentRequest !== requestId) return;

      summaryModal.options[key] = { ...emptyOption(), error: 'generation_failed' };

      addErrorSnackbar({ text: `Failed to generate summary: ${err.message || err}` });
      throw err;
    }
  };

  const openSummaryModal = () => {
    summaryModal.selected = 'default';
    resetOptions();
    summaryModal.isOpen = true;
    loadSummaryOptions();
  };

  const closeSummaryModal = () => {
    summaryModal.isOpen = false;
  };

  const selectSummaryOption = (key) => {
    summaryModal.selected = key;
  };

  const setSummaryContent = (key, mode) => {
    const content = summaryModal.options[key]?.summary;
    if (!content) return;

    const tiptapEditor = tiptap.value[summaryFieldId.value];
    if (tiptapEditor) {
      const html = mode === 'append' ? tiptapEditor.getHTML() + content : content;
      tiptapEditor.chain()
        .setMeta('addToHistory', false)
        .setContent(html, {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: true },
        })
        .run();
      setTimeout(() => { tiptap.emit('update', {}); }, 100);
    }

    summaryModal.isOpen = false;
  };

  const applySummaryContent = (key) => setSummaryContent(key, 'replace');
  const appendSummaryContent = (key) => setSummaryContent(key, 'append');

  return {
    summaryModal,
    openSummaryModal,
    closeSummaryModal,
    loadSummaryOptions,
    retrySummaryOption,
    selectSummaryOption,
    applySummaryContent,
    appendSummaryContent,
  };
});
